'use client';

import { motion } from 'framer-motion';
import { Box, Typography, Container, Button, useTheme } from '@mui/material';
import Link from 'next/link';
import AnimatedBackground from '../AnimatedBackground';

export default function AboutPage() {
  const theme = useTheme();
  
  const revealContainer = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.2,
        ease: 'easeInOut',
      },
    },
  };

  const revealItem = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
      {/* Animated Background */}
      <AnimatedBackground />

      {/* Hero Section */}
      <Box
        component={motion.section}
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 1.2,
          ease: 'easeOut',
        }}
        sx={{
          py: { xs: 8, sm: 12 },
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {/* Glassmorphism Effect for the Hero Box */}
        <Box
          sx={{
            ...theme.custom.mixins.glassmorphism,
            px: { xs: 4, sm: 6 },
            py: { xs: 6, sm: 8 },
            borderRadius: '16px',
            backdropFilter: 'blur(15px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
            mx: 'auto',
            maxWidth: '720px',
          }}
        >
          {/* Animated Title */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 1.5,
              ease: 'easeOut',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                mb: 4,
                background: (theme) => theme.custom.gradients.secondary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 10px rgba(0, 0, 0, 0.2)', // Improved shadow for better readability
              }}
            >
              About Me
            </Typography>
          </motion.div>

          {/* Animated Subtitle */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{
              duration: 1.2,
              delay: 0.5,
              ease: 'easeOut',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                color: (theme) => theme.palette.text.secondary,
                mb: 4,
                textShadow: '0 2px 6px rgba(0, 0, 0, 0.3)', // Subtle shadow for readability
              }}
            >
              From overcoming personal struggles to becoming a self-taught
              software engineer and solutions architect, my journey is one of
              resilience, learning, and innovation.
            </Typography>
          </motion.div>

          {/* Call-to-Action Button */}
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Button
              component={Link}
              href="/contact"
              variant="contained"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'grey.100' },
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              }}
            >
              Get in Touch
            </Button>
          </motion.div>
        </Box>
      </Box>

      {/* My Journey Section */}
      <Box
        component={motion.section}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={revealContainer}
        sx={{
          py: { xs: 8, sm: 12 },
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component={motion.div}
            variants={revealItem}
            sx={{ mb: 4, textAlign: 'center' }}
          >
            My Journey
          </Typography>
          <Typography
            component={motion.p}
            variants={revealItem}
            sx={{ mb: 4, color: (theme) => theme.palette.text.secondary }}
          >
            <strong>I started as an addict... but now I&apos;m an autodidact.</strong> Teaching myself programming patterns and DevOps practices with curiosity and a strong will.
          </Typography>
          <Typography
            component={motion.p}
            variants={revealItem}
            sx={{ mb: 4, color: (theme) => theme.palette.text.secondary }}
          >
            Over the years, I have built a solid foundation in software
            engineering, cloud technologies, containerization, and solutions
            architecture.
          </Typography>
          <Typography
            component={motion.p}
            variants={revealItem}
            sx={{ mb: 4, color: (theme) => theme.palette.text.secondary }}
          >
            Along the way, I balanced being a single father to triplets, an
            experience that taught me the stoic power of patience, planning,
            and prioritization.
          </Typography>
        </Container>
      </Box>
      
      {/* Core Values Section */}
      <Box
        component={motion.section}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={revealContainer}
        sx={{
          py: { xs: 8, sm: 12 },
          px: 2,
          position: 'relative',
          textAlign: 'center',
        }}
      >
        {/* Glassmorphism Effect */}
        <Box
          sx={{
            ...theme.custom.mixins.glassmorphism,
            px: { xs: 4, sm: 6 },
            py: { xs: 6, sm: 8 },
            borderRadius: '16px',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
            mx: 'auto',
            maxWidth: '720px',
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h2"
              component={motion.div}
              variants={revealItem}
              sx={{
                mb: 4,
                textAlign: 'center',
                color: (theme) => theme.palette.text.primary, // Ensure contrast
                textShadow: '0 2px 6px rgba(0, 0, 0, 0.3)', // Subtle shadow
              }}
            >
              Core Values
            </Typography>
            <Box
              component={motion.ul}
              variants={revealContainer}
              sx={{
                pl: 4,
                color: (theme) => theme.palette.text.primary, // Adjust color to match the text
                listStyleType: 'disc',
                textAlign: 'left', // Align the list to the left for better readability
              }}
            >
              <Box
                component={motion.li}
                variants={revealItem}
                sx={{
                  mb: 2, // Add spacing between list items
                }}
              >
                <Typography>
                  <strong>Resilience:</strong> Overcoming challenges defines how I approach work.
                </Typography>
              </Box>
              <Box
                component={motion.li}
                variants={revealItem}
                sx={{
                  mb: 2,
                }}
              >
                <Typography>
                  <strong>Innovation:</strong> Simplicity and innovation go hand-in-hand. I create modular solutions that evolve.
                </Typography>
              </Box>
              <Box
                component={motion.li}
                variants={revealItem}
                sx={{
                  mb: 2,
                }}
              >
                <Typography>
                  <strong>Community:</strong> Sharing knowledge and helping others drives me to publish open-source projects and blog posts.
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Call to Action Section */}
      <Box
        component={motion.section}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={revealContainer}
        sx={{
          py: { xs: 8, sm: 12 },
          textAlign: 'center',
          bgcolor: (theme) => theme.custom.gradients.primary,
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component={motion.div}
            variants={revealItem}
            sx={{ mb: 4 }}
          >
            Let&apos;s Connect
          </Typography>
          <Link href="/contact" passHref>
            <motion.div
              variants={revealItem}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                Get in Touch
              </Button>
            </motion.div>
          </Link>
        </Container>
      </Box>
    </Box>
  );
}